import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';

import { BackendMarketSearchLayout } from '#src/components/market/BackendMarketSearchLayout/index.tsx';
import { MatchdayPicksBanner } from '#src/components/matchdayPicks/MatchdayPicksBanner/index.tsx';
import { MatchdayPicksGrid } from '#src/components/matchdayPicks/MatchdayPicksGrid/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { useMatchdayPicks } from '#src/hooks/primaryBuy/useMatchdayPicks.ts';
import { useBackendFlashSalePicksSearch } from '#src/hooks/search/useBackendFlashSalePicksSearch.ts';
import { messages as searchLayoutMessages } from '#src/searchCards/AdvancedCardSearch/SearchLayout.tsx';
import { backendFlashSalePicksFilters } from '#src/searchCards/football/cardFilters.tsx';

// The rest of the marketplace sorts rank on the sale: `Newly Listed` and `Ending Soon` fail the
// request outright, and `Highest Price`, `Lowest Price` and `Best Value` rank on a `price` the field
// does not reject but a sold out pick's card no longer carries, which would scatter those picks
// through the grid.
const pickSorts: GallerySortNames = [
  'Cards On Sale Popular Player',
  'Cards On Sale Highest Average Score',
  'Cards On Sale Starting Prediction',
];

const DEFAULT_PICK_SORT: GallerySortName = 'Cards On Sale Popular Player';

type Props = {
  sport: Sport;
};

const MatchdayPicksSearchContent = ({ sport }: Props) => {
  const { formatMessage } = useIntl();

  const cardFilters = useMemo(() => backendFlashSalePicksFilters(), []);

  const {
    query,
    page,
    refinements,
    facets,
    advancedFilters,
    sortInputs,
    filtersLoading,
    makeGqlSearchContextValue,
  } = useBackendFlashSalePicksSearch({
    sport,
    cardFilters,
    sorts: pickSorts,
    defaultSort: DEFAULT_PICK_SORT,
  });

  const {
    picks,
    nbHits,
    nbPages,
    page: resultPage,
    facets: resultFacets,
    liveCampaignCount,
    loading,
    refetch,
  } = useMatchdayPicks({
    sport,
    query,
    page,
    advancedFilters,
    refinements,
    facets,
    sorts: sortInputs,
    skip: filtersLoading,
  });

  const onRetry = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <GQLSearchContext
      value={makeGqlSearchContextValue({
        page: resultPage,
        nbPages,
        nbHits,
        facets: resultFacets,
      })}
    >
      <BackendMarketSearchLayout
        banner={!!liveCampaignCount && <MatchdayPicksBanner />}
        sortRowTopPadding
        cardFilters={cardFilters}
        sorts={pickSorts}
        searchBoxPlaceholder={formatMessage(
          searchLayoutMessages.defaultPlaceholder
        )}
      >
        <MatchdayPicksGrid
          picks={picks}
          liveCampaignCount={liveCampaignCount}
          loading={loading || filtersLoading}
          onRetry={onRetry}
        />
      </BackendMarketSearchLayout>
    </GQLSearchContext>
  );
};

export const MatchdayPicksSearch = ({ sport }: Props) => {
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{ sport }}
    >
      <SearchCardsContextProvider
        commonMarket={false}
        manageFavoriteFilter={false}
      >
        <MatchdayPicksSearchContent sport={sport} />
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
